import type { Face } from './rigManifest'
import { CHEER_MS, FLIP_MS } from './runnerPose'
import type { RunnerAnim } from './runnerPose'

/**
 * Cutout-rig poses (docs/art/ART_DIRECTION.md §2a): joint angles for each runner pose over time.
 * Limb angles are radians from hanging straight down, positive = swung forward (towards +x, the
 * running direction). `body` and `head` are tilts, positive = leaning forward.
 */
export interface RigPose {
  body: number
  head: number
  armF: number
  armB: number
  legF: number
  legB: number
  /** Extra height of the whole rig above where it would stand (world px, up = positive). */
  lift: number
  /** Whole-rig turn around its middle (double-jump somersault), positive = clockwise. */
  spin: number
  face: Face
  /** Hip height above the feet line (world px); null = stand on the leg that reaches lowest. */
  hipY: number | null
}

const TAU = Math.PI * 2
/** One full running cycle (two steps). */
const STRIDE_MS = 420
const BREATH_MS = 1400
const HOP_MS = 520
/** Hip height when sitting on the ground (slumped, tucked). */
const SIT_HIP_PX = 5
/** Arms and legs flail at this period while tumbling. */
const FLAIL_MS = 180

const base: RigPose = {
  body: 0,
  head: 0,
  armF: 0.1,
  armB: -0.1,
  legF: 0.08,
  legB: -0.08,
  lift: 0,
  spin: 0,
  face: 'calm',
  hipY: null,
}

/** The pose `t` ms into `anim`. */
export function rigPose(anim: RunnerAnim, t: number): RigPose {
  switch (anim) {
    case 'run': {
      const s = Math.sin((t / STRIDE_MS) * TAU)
      const bounce = Math.abs(Math.cos((t / STRIDE_MS) * TAU))
      return {
        ...base,
        body: 0.18,
        head: -0.06,
        armF: -0.9 * s,
        armB: 0.9 * s,
        legF: 0.75 * s,
        legB: -0.75 * s,
        lift: 2 * bounce,
        face: 'determined',
      }
    }
    case 'jump': {
      const k = Math.min(1, t / 150)
      return {
        ...base,
        body: 0.1,
        armF: 2.5 * k,
        armB: 2.1 * k,
        legF: 1.0 * k,
        legB: -0.35 * k,
        face: 'determined',
      }
    }
    case 'flip':
      // Double jump: a tucked forward somersault.
      return {
        ...base,
        armF: 1.6,
        armB: 1.2,
        legF: 1.5,
        legB: 1.1,
        spin: (TAU * Math.min(t, FLIP_MS)) / FLIP_MS,
        face: 'determined',
      }
    case 'fall':
      return { ...base, armF: 2.7, armB: 2.4, legF: 0.25, legB: -0.15, face: 'surprised' }
    case 'hit': {
      // The renderer tumbles the whole runner (D-045); the limbs flail.
      const s = Math.sin((t / FLAIL_MS) * TAU)
      return {
        ...base,
        armF: 2.2 + 0.5 * s,
        armB: -1.6 - 0.5 * s,
        legF: 0.6 * s,
        legB: -0.6 * s,
        face: 'dizzy',
      }
    }
    case 'win': {
      const s = Math.sin((t / HOP_MS) * TAU)
      return {
        ...base,
        armF: 2.8 + 0.2 * s,
        armB: 2.6 - 0.2 * s,
        legF: 0.2,
        legB: -0.2,
        lift: 10 * Math.max(0, s),
        face: 'overjoyed',
      }
    }
    case 'lose':
      return { ...base, body: 0.12, head: 0.3, armF: 0.02, armB: -0.02, face: 'calm' }
    case 'cheer': {
      // Podium winners: arms up in a V beside the big head, waving, a hop every CHEER_MS.
      const s = Math.sin((t / CHEER_MS) * TAU)
      const wave = 0.25 * Math.sin((t / 260) * TAU)
      return {
        ...base,
        armF: 2.3 + wave,
        armB: -2.3 - wave,
        legF: 0.15,
        legB: -0.15,
        lift: 12 * Math.max(0, s),
        face: 'overjoyed',
      }
    }
    case 'slump':
      // Last place: sitting slumped on the ledge, legs out, head and arms hanging.
      return {
        ...base,
        body: 0.45,
        head: 0.35,
        armF: 0.25,
        armB: 0.15,
        legF: 1.45,
        legB: 1.3,
        face: 'dizzy',
        hipY: SIT_HIP_PX,
      }
    case 'sit':
      // On a ride: the hip is the runner's feet point (put it on the ride's seat), legs astride
      // and forward, hands forward on the reins.
      return {
        ...base,
        body: 0.12,
        armF: 1.1,
        armB: 0.9,
        legF: 1.1,
        legB: 0.8,
        face: 'determined',
        hipY: 0,
      }
    case 'burrow':
      // Drill mole: tucked low and forward while underground.
      return {
        ...base,
        body: 0.9,
        head: -0.3,
        armF: 1.6,
        armB: 1.3,
        legF: 1.3,
        legB: 1.0,
        face: 'determined',
        hipY: SIT_HIP_PX,
      }
    case 'idle': {
      const b = Math.sin((t / BREATH_MS) * TAU)
      return { ...base, armF: 0.1 + 0.04 * b, armB: -0.1 - 0.04 * b, lift: 0.6 * (1 + b) }
    }
  }
}

/**
 * Hip height above the feet line (world px) for a pose: the leg that reaches lowest touches the
 * ground, `hipY` overrides, `lift` raises the whole rig.
 */
export function hipHeight(pose: RigPose, legLength: number): number {
  if (pose.hipY !== null) return pose.hipY + pose.lift
  const reach = Math.max(Math.cos(pose.legF), Math.cos(pose.legB))
  return legLength * reach + pose.lift
}
